import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { ToastProvider } from './components/common/Toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import Layout from './components/common/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import TimeOff from './pages/TimeOff';
import TimeOffSubmit from './pages/TimeOffSubmit';
import Approvals from './pages/Approvals';
import AdminUsers from './pages/admin/Users';
import AdminAllocations from './pages/admin/Allocations';
import Holidays from './pages/admin/Holidays';
import BlackoutDates from './pages/admin/BlackoutDates';
import Help from './pages/Help';
import Tools from './pages/Tools';
import ToolsManage from './pages/admin/ToolsManage';
import AdminSettings from './pages/admin/Settings';
import AdminModules from './pages/admin/Modules';
import AuditLog from './pages/admin/AuditLog';
import AdminNotifications from './pages/admin/Notifications';
import AdminEmailTemplates from './pages/admin/EmailTemplates';
import AdminDepartments from './pages/admin/Departments';
import { LayoutDashboard, Clock, UserCheck, LogOut } from 'lucide-react';
import './styles/app-screens.css';

function LoadingScreen() {
  return (
    <div className="app-loading-screen">
      <div className="app-loading-spinner">
        <Clock size={24} className="app-loading-icon" />
        <span>Loading FPS OS...</span>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="app-error-screen">
      <div className="app-error-screen__title">Something went wrong</div>
      <div>{message}</div>
      <button className="app-error-screen__retry" onClick={() => window.location.reload()}>
        Retry
      </button>
    </div>
  );
}

function PendingSetupScreen() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  const appName = branding?.app_name || 'FPS OS';

  return (
    <div className="app-pending-screen">
      <div className="app-pending-screen__card">
        <div className="app-pending-screen__icon">
          <UserCheck size={40} strokeWidth={1.5} />
        </div>
        <h1 className="app-pending-screen__title">
          Welcome to {appName}!
        </h1>
        <p className="app-pending-screen__greeting">
          Hello, {firstName}
        </p>
        <div className="app-pending-screen__body">
          <p>Your account is being set up by an administrator.</p>
          <p>You will receive access once your department and role have been configured.</p>
        </div>
        <div className="app-pending-screen__hint">
          If you need immediate help, contact your administrator.
        </div>
        <button className="app-pending-screen__signout" onClick={logout}>
          <LogOut size={14} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, requiredRoles }) {
  const { isAuthenticated, loading, authError, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (authError) {
    return <ErrorScreen message={authError} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block pending_setup users from the main app
  if (user?.status === 'pending_setup') {
    return <PendingSetupScreen />;
  }

  // Role-based route protection
  if (requiredRoles && !requiredRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </Layout>
  );
}

function AppRoutes() {
  const { isAuthenticated, loading, authError } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (authError) {
    return <ErrorScreen message={authError} />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Time Off routes */}
      <Route path="/time-off" element={<ProtectedRoute><TimeOff /></ProtectedRoute>} />
      <Route path="/time-off/submit" element={<ProtectedRoute><TimeOffSubmit /></ProtectedRoute>} />

      {/* Approval queue -- managers and admins */}
      <Route path="/approvals" element={<ProtectedRoute requiredRoles={['admin', 'manager']}><Approvals /></ProtectedRoute>} />

      {/* Other protected routes */}
      <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin/users" element={<ProtectedRoute requiredRoles={['admin']}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/import-users" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/allocations" element={<ProtectedRoute requiredRoles={['admin']}><AdminAllocations /></ProtectedRoute>} />
      <Route path="/admin/departments" element={<ProtectedRoute requiredRoles={['admin']}><AdminDepartments /></ProtectedRoute>} />
      <Route path="/admin/holidays" element={<ProtectedRoute requiredRoles={['admin', 'manager']}><Holidays /></ProtectedRoute>} />
      <Route path="/admin/blackout-dates" element={<ProtectedRoute requiredRoles={['admin']}><BlackoutDates /></ProtectedRoute>} />
      <Route path="/admin/tools" element={<ProtectedRoute requiredRoles={['admin', 'manager']}><ToolsManage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute requiredRoles={['admin']}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/modules" element={<ProtectedRoute requiredRoles={['admin']}><AdminModules /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute requiredRoles={['admin']}><AuditLog /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute requiredRoles={['admin']}><AdminNotifications /></ProtectedRoute>} />
      <Route path="/admin/email-templates" element={<ProtectedRoute requiredRoles={['admin']}><AdminEmailTemplates /></ProtectedRoute>} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrandingProvider>
      <ToastProvider>
        <AuthProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </AuthProvider>
      </ToastProvider>
    </BrandingProvider>
  );
}

export default App;
